import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  input,
  output,
  signal,
  Signal,
} from '@angular/core';
import { ButtonComponent } from '@softline/ui-core';
import { DateInputComponent } from '@softline/ui-core/date-input';
import { LabelComponent } from '@softline/ui-core';
import { DsTabsComponent } from '@softline/ui-core/tabs';
import { TabItem } from '@softline/ui-core/tabs';
import {
  ComparisonMode,
  DateRangeComparisonValue,
  DateRangeValue,
  RangeGranularity,
  RangePreset,
} from './date-range-comparison-picker.types';
import {
  computeComparison,
  DEFAULT_FISCAL_YEAR_START,
  FiscalYearStart,
  formatRangeDe,
  fromIsoDate,
  inferGranularity,
  isFiscalYearConfigured,
  rangeForFiscalYear,
  rangeForMonth,
  rangeForPreset,
  rangeForQuarter,
  rangeForYear,
  toIsoDate,
} from './date-range-utils';

interface PresetButton {
  preset: RangePreset;
  label: string;
}

interface ComparisonButton {
  mode: ComparisonMode;
  label: string;
}

const PRESETS: readonly PresetButton[] = [
  { preset: 'yearToDate', label: 'Jahresanfang bis heute' },
  { preset: 'today', label: 'Heute' },
  { preset: 'yesterday', label: 'Gestern' },
  { preset: 'thisWeek', label: 'Diese Woche' },
  { preset: 'thisMonth', label: 'Dieser Monat' },
  { preset: 'fiscalYear', label: 'Geschäftsjahr' },
  { preset: 'allTime', label: 'Ganzer Zeitraum' },
  { preset: 'thisQuarter', label: 'Dieses Quartal' },
  { preset: 'lastQuarter', label: 'Letztes Quartal' },
  { preset: 'thisYear', label: 'Dieses Jahr' },
  { preset: 'lastYear', label: 'Letztes Jahr' },
  { preset: 'last30Days', label: 'Letzte 30 Tage' },
  { preset: 'last90Days', label: 'Letzte 90 Tage' },
];

const COMPARISONS: readonly ComparisonButton[] = [
  { mode: 'previousYear', label: 'Vorjahr' },
  { mode: 'previousPeriod', label: 'Vorperiode' },
  { mode: 'none', label: 'Kein Vergleich' },
  { mode: 'custom', label: 'Benutzerdefiniert' },
];

const GRANULARITY_TABS: readonly TabItem<RangeGranularity>[] = [
  { title: 'Jahr', value: 'year' },
  { title: 'Quartal', value: 'quarter' },
  { title: 'Monat', value: 'month' },
  { title: 'Indiv.', value: 'custom' },
];

const GRANULARITY_TABS_WITH_FY: readonly TabItem<RangeGranularity>[] = [
  { title: 'Jahr', value: 'year' },
  { title: 'GJ', value: 'fiscalYear' },
  { title: 'Quartal', value: 'quarter' },
  { title: 'Monat', value: 'month' },
  { title: 'Indiv.', value: 'custom' },
];

let nextPopoverInputId = 0;

@Component({
  selector: 'soft-ds-date-range-comparison-picker-popover',
  imports: [ButtonComponent, DateInputComponent, DsTabsComponent, LabelComponent],
  templateUrl: './date-range-comparison-picker-popover.component.html',
  styleUrls: ['./date-range-comparison-picker-popover.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeComparisonPickerPopoverComponent {
  readonly initialValue = input<DateRangeComparisonValue | null>(null);
  readonly fiscalYearStart = input<FiscalYearStart>(DEFAULT_FISCAL_YEAR_START);
  readonly referenceDate = input<Date>(new Date());

  readonly apply = output<DateRangeComparisonValue>();
  readonly cancelled = output<void>();

  // Working draft. Edited inside the popover; emitted on Übernehmen.
  // Defaults to the current calendar year (Jahr tab) so the picker opens on a
  // year selection rather than the narrowest month view.
  private readonly draftRange = signal<DateRangeValue>(
    rangeForYear(new Date().getFullYear())
  );
  private readonly draftPreset = signal<RangePreset | null>(null);
  private readonly draftGranularity = signal<RangeGranularity>('year');
  private readonly draftComparisonMode = signal<ComparisonMode>('previousYear');
  private readonly draftComparisonCustom = signal<DateRangeValue | null>(null);

  // Year/quarter/month picker state — kept independent of the draft so
  // switching granularity preserves the user's place.
  private readonly selectedYear = signal<number>(new Date().getFullYear());
  private readonly selectedQuarter = signal<1 | 2 | 3 | 4>(1);
  private readonly selectedMonth = signal<number>(new Date().getMonth());
  /** End-year of the currently selected fiscal year (e.g. 2026 for FY 25/26). */
  private readonly selectedFiscalYear = signal<number>(new Date().getFullYear());
  // Tracks whether the Shift modifier is currently held. Listened on the
  // host because the popover gets focused immediately on open, so window-
  // level keyboard input is reliably captured here.
  private readonly shiftPressed = signal(false);
  // Anchor for shift-click range selection. Set on every plain click;
  // shift-click extends the range from the anchor to the new target.
  // Stored as a {year, index} pair where `index` is 1..4 for quarters or
  // 0..11 for months — kept granularity-agnostic so the same anchor can
  // serve either picker after a granularity tab switch.
  private readonly anchor = signal<{ year: number; index: number } | null>(null);

  protected readonly granularityTabs = computed(() =>
    isFiscalYearConfigured(this.fiscalYearStart())
      ? GRANULARITY_TABS_WITH_FY
      : GRANULARITY_TABS
  );
  protected readonly presets = PRESETS;
  protected readonly comparisons = COMPARISONS;
  private readonly customInputIdPrefix = `soft-ds-drcp-custom-${nextPopoverInputId++}`;
  protected readonly customFromInputId = `${this.customInputIdPrefix}-from`;
  protected readonly customToInputId = `${this.customInputIdPrefix}-to`;
  protected readonly customCompFromInputId = `${this.customInputIdPrefix}-comparison-from`;
  protected readonly customCompToInputId = `${this.customInputIdPrefix}-comparison-to`;

  protected readonly years = computed(() => {
    const current = this.referenceDate().getFullYear();
    return [0, 1, 2, 3, 4].map((offset) => current - offset);
  });

  protected readonly quarters = [1, 2, 3, 4] as const;
  protected readonly months = Array.from({ length: 12 }, (_, i) => i);

  protected readonly granularityValue = this.draftGranularity.asReadonly();
  protected readonly comparisonModeValue = this.draftComparisonMode.asReadonly();

  protected readonly draftComparison: Signal<DateRangeValue | null> = computed(() =>
    computeComparison(
      this.draftRange(),
      this.draftComparisonMode(),
      this.draftComparisonCustom()
    )
  );

  protected readonly summaryRange = computed(() => formatRangeDe(this.draftRange()));
  protected readonly summaryComparison = computed(() => {
    const c = this.draftComparison();
    return c ? formatRangeDe(c) : '—';
  });

  protected readonly customFromIso = computed(() => toIsoDate(this.draftRange().from));
  protected readonly customToIso = computed(() => toIsoDate(this.draftRange().to));
  protected readonly customCompFromIso = computed(() =>
    toIsoDate(this.draftComparisonCustom()?.from ?? null)
  );
  protected readonly customCompToIso = computed(() =>
    toIsoDate(this.draftComparisonCustom()?.to ?? null)
  );

  protected readonly isCustomGranularity = computed(
    () => this.draftGranularity() === 'custom'
  );
  protected readonly isCustomComparison = computed(
    () => this.draftComparisonMode() === 'custom'
  );

  constructor() {
    // Hydrate the draft from `initialValue` whenever it changes.
    effect(() => {
      const init = this.initialValue();
      if (!init) return;
      this.draftRange.set(init.range);
      this.draftPreset.set(init.preset);
      this.draftGranularity.set(init.granularity);
      this.draftComparisonMode.set(init.comparisonMode);
      this.draftComparisonCustom.set(
        init.comparisonMode === 'custom' ? init.comparison : null
      );
      this.selectedYear.set(init.range.from.getFullYear());
      this.selectedQuarter.set(
        (Math.floor(init.range.from.getMonth() / 3) + 1) as 1 | 2 | 3 | 4
      );
      this.selectedMonth.set(init.range.from.getMonth());
      // Seed the anchor at the start of the hydrated range. `index` semantics
      // depend on granularity — for quarter it's 1..4, for month it's 0..11.
      const startMonth = init.range.from.getMonth();
      this.anchor.set({
        year: init.range.from.getFullYear(),
        index:
          init.granularity === 'quarter' ? Math.floor(startMonth / 3) + 1 : startMonth,
      });
    });
  }

  // Read Shift modifier directly from the mousedown event that immediately
  // precedes a click. Bubbles up from any button inside the popover host
  // (we don't bind on the buttons themselves because they're DS components
  // that don't forward MouseEvent on their `click` output). One bubbled
  // host listener per popover instance — no global keyboard tracking, no
  // out-of-zone subscriptions, no CD storms.
  @HostListener('mousedown', ['$event'])
  protected onHostMouseDown(event: MouseEvent): void {
    this.shiftPressed.set(event.shiftKey);
  }

  // Same idea for keyboard activation (Enter/Space on a focused button —
  // KeyboardEvent also exposes `shiftKey`). Captures the modifier just
  // before the synthetic click fires.
  @HostListener('keydown', ['$event'])
  protected onHostKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      this.shiftPressed.set(event.shiftKey);
    }
  }

  protected isPresetActive(preset: RangePreset): boolean {
    if (this.draftPreset() === preset) return true;
    // Fall back to range-shape match so a hydrated value with no explicit
    // preset still highlights whichever preset its range matches.
    const candidate = rangeForPreset(
      preset,
      this.referenceDate(),
      this.fiscalYearStart()
    );
    const draft = this.draftRange();
    return (
      candidate.from.getTime() === draft.from.getTime() &&
      candidate.to.getTime() === draft.to.getTime()
    );
  }

  // Precomputed active sets — recompute once per draft change instead of
  // re-running `inferGranularity` + `rangeForQuarter`/`rangeForMonth` in
  // every `is*Active` call across every CD pass. With ~30 buttons in the
  // popover each rebinding `[variant]` on every CD, the savings add up.
  private readonly activeQuarters = computed<ReadonlySet<number>>(() => {
    if (this.draftGranularity() !== 'quarter') return new Set();
    const year = this.selectedYear();
    const draft = this.draftRange();
    const set = new Set<number>();
    for (const q of [1, 2, 3, 4] as const) {
      const r = rangeForQuarter(year, q);
      if (r.from >= draft.from && r.to <= draft.to) set.add(q);
    }
    return set;
  });

  private readonly activeMonths = computed<ReadonlySet<number>>(() => {
    if (this.draftGranularity() !== 'month') return new Set();
    const year = this.selectedYear();
    const draft = this.draftRange();
    const set = new Set<number>();
    for (let m = 0; m < 12; m++) {
      const r = rangeForMonth(year, m);
      if (r.from >= draft.from && r.to <= draft.to) set.add(m);
    }
    return set;
  });

  protected isYearActive(y: number): boolean {
    return this.selectedYear() === y;
  }

  protected isQuarterActive(q: 1 | 2 | 3 | 4): boolean {
    return this.activeQuarters().has(q);
  }

  protected isMonthActive(m: number): boolean {
    return this.activeMonths().has(m);
  }

  /** End-year of the 5 most recent fiscal years (current at index 0). */
  protected readonly fiscalYears = computed(() => {
    const fiscal = this.fiscalYearStart();
    const ref = this.referenceDate();
    // The "current" fiscal year is the one whose range contains `ref`.
    // For Sep-1 fiscal start: dates Jan-Aug 2026 are still inside FY 25/26
    // (which ends Aug 31 2026), so the end-year is `ref.getFullYear()`
    // when `ref` falls before the next FY start, otherwise `ref` year + 1.
    const candidate = new Date(ref.getFullYear(), fiscal.month - 1, fiscal.day);
    const currentEndYear = ref < candidate ? ref.getFullYear() : ref.getFullYear() + 1;
    return [0, 1, 2, 3, 4].map((offset) => currentEndYear - offset);
  });

  protected isFiscalYearActive(endYear: number): boolean {
    return (
      this.draftGranularity() === 'fiscalYear' && this.selectedFiscalYear() === endYear
    );
  }

  protected fiscalYearLabel(endYear: number): string {
    return `GJ ${String(endYear - 1).slice(-2)}/${String(endYear).slice(-2)}`;
  }

  protected isComparisonActive(mode: ComparisonMode): boolean {
    return this.draftComparisonMode() === mode;
  }

  protected onPresetClick(preset: RangePreset): void {
    const range = rangeForPreset(preset, this.referenceDate(), this.fiscalYearStart());
    this.draftRange.set(range);
    this.draftPreset.set(preset);
    const granularity = inferGranularity(range);
    this.draftGranularity.set(granularity);
    if (preset === 'today' || preset === 'yesterday') {
      this.draftComparisonMode.set('previousPeriod');
      this.draftComparisonCustom.set(null);
    }
    this.selectedYear.set(range.from.getFullYear());
    this.selectedMonth.set(range.from.getMonth());
    this.selectedQuarter.set(
      (Math.floor(range.from.getMonth() / 3) + 1) as 1 | 2 | 3 | 4
    );
    if (granularity === 'quarter') {
      this.anchor.set({
        year: range.from.getFullYear(),
        index: Math.floor(range.from.getMonth() / 3) + 1,
      });
    } else if (granularity === 'month') {
      this.anchor.set({
        year: range.from.getFullYear(),
        index: range.from.getMonth(),
      });
    } else {
      this.anchor.set(null);
    }
  }

  protected onGranularityChange(value: RangeGranularity | null): void {
    if (!value) return;
    this.draftGranularity.set(value);
    this.draftPreset.set(null);
    const year = this.selectedYear();
    if (value === 'year') {
      this.draftRange.set(rangeForYear(year));
      this.anchor.set(null);
    } else if (value === 'fiscalYear') {
      this.draftRange.set(
        rangeForFiscalYear(this.selectedFiscalYear(), this.fiscalYearStart())
      );
      this.anchor.set(null);
    } else if (value === 'quarter') {
      const q = this.selectedQuarter();
      this.draftRange.set(rangeForQuarter(year, q));
      this.anchor.set({ year, index: q });
    } else if (value === 'month') {
      const m = this.selectedMonth();
      this.draftRange.set(rangeForMonth(year, m));
      this.anchor.set({ year, index: m });
    } else {
      this.anchor.set(null);
    }
  }

  protected onFiscalYearClick(endYear: number): void {
    this.selectedFiscalYear.set(endYear);
    this.draftGranularity.set('fiscalYear');
    this.draftPreset.set(null);
    this.draftRange.set(rangeForFiscalYear(endYear, this.fiscalYearStart()));
    this.anchor.set(null);
  }

  protected onYearClick(y: number): void {
    this.selectedYear.set(y);
    this.draftPreset.set(null);
    const g = this.draftGranularity();
    if (g === 'year') this.draftRange.set(rangeForYear(y));
    else if (g === 'quarter')
      this.draftRange.set(rangeForQuarter(y, this.selectedQuarter()));
    else if (g === 'month') this.draftRange.set(rangeForMonth(y, this.selectedMonth()));
  }

  protected onQuarterClick(q: 1 | 2 | 3 | 4): void {
    this.draftGranularity.set('quarter');
    this.draftPreset.set(null);
    const year = this.selectedYear();
    const anchor = this.anchor();
    if (this.shiftPressed() && anchor) {
      // Span from anchor → click. `anchor.index` is 1..4 (quarter number).
      // Spans across years are supported by computing the absolute quarter
      // index (year * 4 + (q - 1)) and ordering by that.
      const a = anchor.year * 4 + (anchor.index - 1);
      const b = year * 4 + (q - 1);
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const loYear = Math.floor(lo / 4);
      const loQuarter = ((lo % 4) + 1) as 1 | 2 | 3 | 4;
      const hiYear = Math.floor(hi / 4);
      const hiQuarter = ((hi % 4) + 1) as 1 | 2 | 3 | 4;
      const loRange = rangeForQuarter(loYear, loQuarter);
      const hiRange = rangeForQuarter(hiYear, hiQuarter);
      this.draftRange.set({ from: loRange.from, to: hiRange.to });
      this.selectedQuarter.set(q);
      // Don't update the anchor — keeps the same anchor for subsequent
      // shift-clicks ("extend further" UX, not "redefine the anchor").
      return;
    }
    // Plain click: single quarter, set as new anchor.
    this.selectedQuarter.set(q);
    this.anchor.set({ year, index: q });
    this.draftRange.set(rangeForQuarter(year, q));
  }

  protected onMonthClick(m: number): void {
    this.draftGranularity.set('month');
    this.draftPreset.set(null);
    const year = this.selectedYear();
    const anchor = this.anchor();
    if (this.shiftPressed() && anchor) {
      // `anchor.index` is 0..11 (month index). Same absolute-index trick
      // as quarters, but with 12 months per year.
      const a = anchor.year * 12 + anchor.index;
      const b = year * 12 + m;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const loYear = Math.floor(lo / 12);
      const loMonth = lo % 12;
      const hiYear = Math.floor(hi / 12);
      const hiMonth = hi % 12;
      const loRange = rangeForMonth(loYear, loMonth);
      const hiRange = rangeForMonth(hiYear, hiMonth);
      this.draftRange.set({ from: loRange.from, to: hiRange.to });
      this.selectedMonth.set(m);
      return;
    }
    this.selectedMonth.set(m);
    this.anchor.set({ year, index: m });
    this.draftRange.set(rangeForMonth(year, m));
  }

  protected onComparisonClick(mode: ComparisonMode): void {
    this.draftComparisonMode.set(mode);
    if (mode !== 'custom') {
      this.draftComparisonCustom.set(null);
    }
  }

  protected onCustomFromChange(iso: string | null): void {
    const from = fromIsoDate(iso);
    if (!from) return;
    const current = this.draftRange();
    const to = current.to < from ? from : current.to;
    this.draftRange.set({ from, to });
    this.draftPreset.set(null);
    this.draftGranularity.set('custom');
  }

  protected onCustomToChange(iso: string | null): void {
    const to = fromIsoDate(iso);
    if (!to) return;
    const current = this.draftRange();
    const from = current.from > to ? to : current.from;
    this.draftRange.set({ from, to });
    this.draftPreset.set(null);
    this.draftGranularity.set('custom');
  }

  protected onCustomCompFromChange(iso: string | null): void {
    const from = fromIsoDate(iso);
    if (!from) return;
    const current = this.draftComparisonCustom();
    const to = current?.to && current.to >= from ? current.to : from;
    this.draftComparisonCustom.set({ from, to });
  }

  protected onCustomCompToChange(iso: string | null): void {
    const to = fromIsoDate(iso);
    if (!to) return;
    const current = this.draftComparisonCustom();
    const from = current?.from && current.from <= to ? current.from : to;
    this.draftComparisonCustom.set({ from, to });
  }

  protected onReset(): void {
    this.draftPreset.set(null);
    this.draftGranularity.set('custom');
    this.draftComparisonMode.set('previousYear');
    this.draftComparisonCustom.set(null);
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }

  protected onApply(): void {
    this.apply.emit({
      range: this.draftRange(),
      comparison: this.draftComparison(),
      granularity: this.draftGranularity(),
      preset: this.draftPreset(),
      comparisonMode: this.draftComparisonMode(),
    });
  }
}
